import { Dispatch } from "redux";
import { postPollCreation, updatePoll } from "@Services/API/Polls";
import { ReportService } from "@Services/API/Reports";
import {
    postChannel,
    postDeleteChannel,
    getUserChannels,
    getChannelPolls,
    putChannel,
    postSubChannel,
    getChannelById,
    postCloneChannel,
} from "@Services/API/Channels";
import {
    updatePollTextField as updatePollTextFieldApi,
    deletePollTextField as deletePollTextFieldApi,
} from "@Services/API/PollTextField";
import {
    CreateChannelRequest,
    UpdateChannelRequest,
    CreateSubChannelRequest,
    CreatePollRequest,
    UpdatePollRequest,
    IChannel,
    IVisualizationChart,
    ITotalNumbersStats,
} from "@Interfaces";
import { UpdateChannelResponse } from "@Services/API/Channels/interfaces";
import { AppThunk } from "@Store";
import { actions } from "./index";
import {
    CreateSharedLinkRequest,
    CreateSharedLinkPayload,
} from "@Services/API/Links/interfaces";
import { postSharedLink, getLinkData, postLinkScan } from "@Services/API/Links";
import { NotificationActions, RootStateType } from "@Store";
import { actions as dialogActions } from "@Store/Reducers/dialog";
import { resolveRedirectTarget } from "@Utils/redirectUrl";
import { DialogComponents } from "@Interfaces/Entities/Dialogs";
import { HttpModel } from "@Interfaces";
import { filterActiveChannels } from "@Utils/channelFilters";
import { hasPremiumFeatures } from "@Utils/accountEntitlements";
import { history } from "src/history";
import i18n from "src/i18n";
import {
    postAssistantMessage,
    PostAssistantMessageResponse,
} from "@Services/API/Assistant";
import { ChartSpec } from "@Interfaces/Entities/AssistantCharts";
import { ChannelContextAvailabilityResponse } from "@Services/API/Reports";
import {
    loadAssistantContextIds,
    MAX_ASSISTANT_CONTEXT_IDS,
    saveAssistantContextIds,
} from "../../../Utils/assistantContextStorage";

const inflightDetailsRequests: Record<string, Promise<any>> = {};
const inflightAssistantContextAvailabilityRequests: Partial<
    Record<
        string,
        Promise<{
            status: "succeeded" | "failed";
            hasAnalysisBundle: boolean;
            title?: string;
        }>
    >
> = {};

const getErrorMessage = (error: unknown, fallback: string): string => {
    const httpError = error as HttpModel.HttpError;

    if (httpError?.response?.data?.message) {
        return String(httpError.response.data.message);
    }

    if (httpError?.message) {
        return String(httpError.message);
    }

    if (error instanceof Error && error.message) {
        return error.message;
    }

    return fallback;
};

const {
    addPollInChannel,
    createChannel,
    createSubChannel,
    updateChannel,
    upsertChannel,
    removeChannel,
    storeChannels,
    storePolls,
    setChannelDetailsStatus,
    storeSharedLinkInChannel,
    isChannelActionLoaded,
    storeInvitationChannel,
    removeInvitationChannel,
    isChannelActionLoading,
    setActiveChannelId,
    setSelectedAssistantContexts,
    setAssistantContextAvailability,
    updatePollInChannel,
} = actions;

const ChannelActions = {
    createPoll: (pollData: CreatePollRequest) => async (dispatch: Dispatch) => {
        try {
            const { data: response } = await postPollCreation(pollData);

            const pollDataFormated = {
                ...response,
                multi_voting:
                    typeof response?.multi_voting === "boolean"
                        ? response.multi_voting
                        : !!pollData.multi_voting,
                comments: {
                    count: 0,
                    list: [],
                    isLoading: false,
                    isExpanded: false,
                    loadMore: false,
                },
            };

            dispatch(
                addPollInChannel({
                    poll: pollDataFormated,
                    channel_id: pollData.channel_id,
                })
            );
        } catch (error) {
            throw new Error(getErrorMessage(error, "Failed to create poll"));
        }
    },
    setActiveChannel: (channelId: string) => (dispatch: Dispatch) => {
        dispatch(setActiveChannelId(channelId));
    },

    editPoll:
        (pollId: string, payload: UpdatePollRequest) =>
        async (dispatch: Dispatch) => {
            try {
                const response: any = await updatePoll(pollId, payload);
                const poll = response.data;
                const formattedPoll = {
                    ...poll,
                    comments: {
                        count: poll.comments.length,
                        list: poll.comments,
                        isLoading: false,
                        isExpanded: false,
                        loadMore: false,
                    },
                };

                dispatch(updatePollInChannel({ poll: formattedPoll }));
            } catch (error) {
                console.error(error);
            }
        },

    createChannel:
        (payload: CreateChannelRequest) => async (dispatch: Dispatch) => {
            try {
                // dummy poll
                const { data } = await postChannel(payload);

                history.push("/channel/" + data._id);
                const defaultAttributes = {
                    polls: [],
                    shared_links: [],
                };

                dispatch(
                    createChannel({
                        ...data,
                        ...defaultAttributes,
                        has_children: data.has_children ?? false,
                        sub_channels: data.sub_channels ?? null,
                        isHydrated: true,
                        pollsStatus: "idle",
                        detailsStatus: "succeeded",
                    })
                );
            } catch (err) {
                const error = err as HttpModel.HttpError;

                if (error?.status === 429) {
                    dispatch(
                        NotificationActions.notification({
                            snackbarOpen: true,
                            snackbarType: "error",
                            snackbarMessage: "You have reached your limits",
                        })
                    );
                } else {
                    dispatch(
                        NotificationActions.notification({
                            snackbarOpen: true,
                            snackbarType: "error",
                            snackbarMessage:
                                "An unexpected error occurred. Please try again.",
                        })
                    );
                }
            }
        },
    updateChannel:
        (channel: IChannel, data: UpdateChannelRequest) =>
        async (dispatch: Dispatch) => {
            try {
                const response: any = await putChannel(channel, data);
                dispatch(updateChannel(response.data));
            } catch (error) {
                throw new Error(
                    getErrorMessage(error, "Failed to update channel")
                );
            }
        },
    createSubChannel:
        (parentChannel: IChannel, payload: CreateSubChannelRequest) =>
        async (dispatch: Dispatch) => {
            try {
                const { data } = await postSubChannel(
                    parentChannel._id,
                    payload
                );

                const defaultAttributes = {
                    polls: [],
                    shared_links: [],
                    has_children: false,
                    sub_channels: null,
                };

                dispatch(
                    createSubChannel({
                        subChannel: {
                            ...data,
                            ...defaultAttributes,
                            parent_channel_id:
                                data.parent_channel_id || parentChannel._id,
                            isHydrated: true,
                            pollsStatus: "idle",
                            detailsStatus: "succeeded",
                        },
                        parentId: parentChannel._id,
                    })
                );
            } catch (error) {
                throw new Error(
                    getErrorMessage(error, "Failed to create sub-channel")
                );
            }
        },
    fetchChannelById: (channelId: string) => async (dispatch: any) => {
        if (!channelId) return undefined;

        if (await inflightDetailsRequests[channelId]) {
            return inflightDetailsRequests[channelId];
        }

        const requestPromise = (async () => {
            try {
                dispatch(
                    setChannelDetailsStatus({ channelId, status: "loading" })
                );
                const { data } = await getChannelById(channelId);
                const defaultAttributes = {
                    polls: data.polls ?? [],
                    shared_links: data.shared_links ?? [],
                };

                dispatch(
                    upsertChannel({
                        ...defaultAttributes,
                        ...data,
                        isHydrated: true,
                        detailsStatus: "succeeded",
                    })
                );

                return data as IChannel;
            } catch (error) {
                dispatch(
                    setChannelDetailsStatus({ channelId, status: "failed" })
                );
                throw error;
            } finally {
                delete inflightDetailsRequests[channelId];
            }
        })();

        inflightDetailsRequests[channelId] = requestPromise;

        return requestPromise;
    },

    deleteChannel: (channel: IChannel) => async (dispatch: Dispatch) => {
        try {
            await postDeleteChannel(channel._id);
            dispatch(removeChannel(channel));

            history.push("/");

            dispatch(
                NotificationActions.notification({
                    snackbarOpen: true,
                    snackbarType: "success",
                    snackbarMessage: "Event has been succesfuly removed.",
                })
            );
        } catch (error) {
            dispatch(
                NotificationActions.notification({
                    snackbarOpen: true,
                    snackbarType: "error",
                    snackbarMessage:
                        "Oops something went wrong, unable to delete the channel.",
                })
            );
        }
    },

    cloneChannel:
        (payload: {
            group_id: string;
            overrides?: {
                title?: string;
                description?: string;
                profile_image?: string;
                cover_image?: string;
                parent_channel_id?: string | null;
            };
        }) =>
        async (dispatch: Dispatch) => {
            try {
                dispatch(isChannelActionLoading());

                const { data } = await postCloneChannel(
                    payload.group_id,
                    payload.overrides
                );

                const defaultAttributes: Pick<
                    IChannel,
                    | "polls"
                    | "shared_links"
                    | "has_children"
                    | "sub_channels"
                    | "isHydrated"
                    | "pollsStatus"
                    | "detailsStatus"
                > = {
                    polls: [],
                    shared_links: [],
                    has_children: data.has_children ?? false,
                    sub_channels: data.sub_channels ?? null,
                    isHydrated: true,
                    pollsStatus: "idle",
                    detailsStatus: "succeeded",
                };

                const clonedChannel: IChannel = {
                    ...data,
                    ...defaultAttributes,
                    parent_channel_id: data.parent_channel_id ?? null,
                };

                // If this is a sub-channel clone, also update the parent summary list.
                if (data.parent_channel_id) {
                    dispatch(
                        createSubChannel({
                            subChannel: clonedChannel,
                            parentId: String(data.parent_channel_id),
                        })
                    );
                } else {
                    dispatch(createChannel(clonedChannel));
                }

                await (dispatch as any)(
                    ChannelActions.fetchChannelPolls(data._id)
                );

                dispatch(
                    NotificationActions.notification({
                        snackbarOpen: true,
                        snackbarType: "success",
                        snackbarMessage: "Channel cloned successfully!",
                    })
                );

                // Navigate to the new cloned channel
                history.push("/channel/" + data._id);

                dispatch(isChannelActionLoaded());

                return data;
            } catch (err) {
                dispatch(isChannelActionLoaded());

                const error = err as HttpModel.HttpError;

                let errorMessage = "Failed to clone channel. Please try again.";

                if (error?.response?.data?.message) {
                    errorMessage = error.response.data.message;
                }

                dispatch(
                    NotificationActions.notification({
                        snackbarOpen: true,
                        snackbarType: "error",
                        snackbarMessage: errorMessage,
                    })
                );

                throw new Error(errorMessage);
            }
        },

    // Action of fetch all the channels
    fetchChannels: () => async (dispatch: Dispatch) => {
        try {
            const { data } = await getUserChannels();
            dispatch(storeChannels(data));
        } catch (error) {
            const errorMessage = getErrorMessage(
                error,
                "Failed to fetch channels"
            );
            console.error("[ChannelActions.fetchChannels]", error);
            dispatch(
                NotificationActions.notification({
                    snackbarOpen: true,
                    snackbarType: "error",
                    snackbarMessage: errorMessage,
                })
            );
        }
    },

    // Action of fetch all the polls
    fetchChannelPolls:
        (channel_id: string) =>
        async (dispatch: any, getState: () => RootStateType) => {
            if (!channel_id) return;

            try {
                const { data, text_fields } = await getChannelPolls(channel_id);

                const pollState = (Array.isArray(data) ? data : []).map(
                    (poll: any) => ({
                        ...poll,
                        //@todo refactor avoid repeation
                        comments: {
                            count: poll.comments.length,
                            list: poll.comments,
                            isLoading: false,
                            isExpanded: false,
                            loadMore: false,
                        },
                    })
                );
                const textFieldState = Array.isArray(text_fields)
                    ? text_fields
                    : [];

                dispatch(
                    storePolls({
                        polls: pollState,
                        channel_id,
                        text_fields: textFieldState,
                    })
                );
            } catch (error) {
                const errorMessage = getErrorMessage(
                    error,
                    "Failed to fetch channel polls"
                );
                console.error("[ChannelActions.fetchChannelPolls]", error);
                dispatch(
                    NotificationActions.notification({
                        snackbarOpen: true,
                        snackbarType: "error",
                        snackbarMessage: errorMessage,
                    })
                );
            }
        },

    ensureChannelData:
        (channelId: string) =>
        async (dispatch: any, getState: () => RootStateType) => {
            if (!channelId) return;

            const channelState = getState().channels;
            const existingChannel = channelState.channels.find(
                (channel) => channel._id === channelId
            );

            const shouldFetchDetails =
                !existingChannel ||
                existingChannel.detailsStatus !== "succeeded" ||
                !existingChannel.isHydrated;

            if (shouldFetchDetails) {
                await dispatch(ChannelActions.fetchChannelById(channelId));
            }

            const updatedChannel = getState().channels.channels.find(
                (channel) => channel._id === channelId
            );

            const shouldFetchPolls =
                !!updatedChannel &&
                (!Array.isArray(updatedChannel.polls) ||
                    updatedChannel.polls.length === 0 ||
                    updatedChannel.pollsStatus === "idle" ||
                    !updatedChannel.text_fields);

            if (shouldFetchPolls) {
                await dispatch(ChannelActions.fetchChannelPolls(channelId));
            }
        },

    fetchChannelSummary:
        (channelId: string): AppThunk<Promise<string | null>> =>
        async () => {
            try {
                const response = await ReportService.getChannelSummary(
                    channelId
                );

                // Prefer the structured short summary when available.
                const ss = response.short_summary;
                if (ss) {
                    const parts: string[] = [];

                    // Core tension is a single string describing the main audience divide.
                    if (ss.core_tension) parts.push(String(ss.core_tension));

                    // Top findings is a list of key takeaways — render as a bullet list.
                    const findings = ss.top_findings as string[] | undefined;
                    if (findings?.length) {
                        parts.push(findings.map((f) => `• ${f}`).join("\n"));
                    }

                    // Demographic highlight is an optional callout about audience segments.
                    if (ss.demographic_highlight) {
                        parts.push(String(ss.demographic_highlight));
                    }

                    const shortSummary = parts.join("\n\n").trim();
                    if (shortSummary) return shortSummary;
                }

                // Fall back to the legacy summary field when short_summary is absent or empty.
                return response.executive_summary ?? null;
            } catch {
                return null;
            }
        },

    fetchAssistantContextAvailability:
        (
            channelId: string
        ): AppThunk<
            Promise<{
                status: "succeeded" | "failed";
                hasAnalysisBundle: boolean;
                title?: string;
            }>
        > =>
        async (dispatch, getState) => {
            if (!channelId) {
                return {
                    status: "failed",
                    hasAnalysisBundle: false,
                };
            }

            const existingAvailability =
                getState().channels.assistantContextAvailabilityById[channelId];

            if (existingAvailability?.status === "succeeded") {
                return {
                    status: "succeeded",
                    hasAnalysisBundle: existingAvailability.hasAnalysisBundle,
                    title: existingAvailability.title,
                };
            }

            if (inflightAssistantContextAvailabilityRequests[channelId]) {
                return inflightAssistantContextAvailabilityRequests[channelId];
            }

            dispatch(
                setAssistantContextAvailability({
                    channelId,
                    status: "loading",
                    hasAnalysisBundle:
                        existingAvailability?.hasAnalysisBundle ?? false,
                    title: existingAvailability?.title,
                })
            );

            const requestPromise = (async () => {
                try {
                    const response: ChannelContextAvailabilityResponse =
                        await ReportService.getChannelContextAvailability(
                            channelId
                        );

                    const nextAvailability = {
                        status: "succeeded" as const,
                        hasAnalysisBundle: Boolean(
                            response.has_analysis_bundle
                        ),
                        title: response.title,
                    };

                    dispatch(
                        setAssistantContextAvailability({
                            channelId,
                            ...nextAvailability,
                        })
                    );

                    return nextAvailability;
                } catch {
                    const failedAvailability = {
                        status: "failed" as const,
                        hasAnalysisBundle: false,
                        title: existingAvailability?.title,
                    };

                    dispatch(
                        setAssistantContextAvailability({
                            channelId,
                            ...failedAvailability,
                        })
                    );

                    return failedAvailability;
                } finally {
                    delete inflightAssistantContextAvailabilityRequests[
                        channelId
                    ];
                }
            })();

            inflightAssistantContextAvailabilityRequests[channelId] =
                requestPromise;

            return requestPromise;
        },

    prefetchAssistantContextAvailability:
        (channelIds: string[]): AppThunk<Promise<void>> =>
        async (dispatch, getState) => {
            const uniqueChannelIds = Array.from(
                new Set(channelIds.filter(Boolean))
            );

            const channelIdsToFetch = uniqueChannelIds.filter((channelId) => {
                const availability =
                    getState().channels.assistantContextAvailabilityById[
                        channelId
                    ];

                return (
                    !availability ||
                    (availability.status !== "succeeded" &&
                        availability.status !== "loading")
                );
            });

            await Promise.all(
                channelIdsToFetch.map((contextChannelId) =>
                    dispatch(
                        ChannelActions.fetchAssistantContextAvailability(
                            contextChannelId
                        )
                    )
                )
            );
        },

    loadAssistantContexts:
        (channelId: string): AppThunk<Promise<string[]>> =>
        async (dispatch) => {
            const contextIds = loadAssistantContextIds(channelId);

            dispatch(
                setSelectedAssistantContexts({
                    channelId,
                    contextIds,
                })
            );

            return contextIds;
        },

    saveAssistantContexts:
        ({
            channelId,
            contextIds,
        }: {
            channelId: string;
            contextIds: string[];
        }): AppThunk<Promise<string[]>> =>
        async (dispatch) => {
            const normalizedContextIds = saveAssistantContextIds(
                channelId,
                contextIds
            );

            dispatch(
                setSelectedAssistantContexts({
                    channelId,
                    contextIds: normalizedContextIds,
                })
            );

            return normalizedContextIds;
        },

    sendAssistantChatMessage:
        ({
            channelId,
            contextChannelIds = [],
            prompt,
        }: {
            channelId: string;
            contextChannelIds?: string[];
            prompt: string;
        }): AppThunk<
            Promise<{
                text?: string;
                errorMessage?: string;
                charts: ChartSpec[];
            }>
        > =>
        async () => {
            try {
                // Keep FE aligned with backend guard: assistant context is capped to 5 ids.
                // This is a defensive trim in case stale local state contains more entries.
                const normalizedContextChannelIds = contextChannelIds.slice(
                    0,
                    MAX_ASSISTANT_CONTEXT_IDS
                );

                const response: PostAssistantMessageResponse =
                    await postAssistantMessage({
                        channel_id: channelId,
                        // Send only the capped, normalized context list.
                        context_channel_ids: normalizedContextChannelIds,
                        message: prompt,
                    });
                return {
                    text: response.text,
                    errorMessage: response.error_message,
                    charts: response.charts ?? [],
                };
            } catch (error) {
                throw new Error(
                    getErrorMessage(error, "assistant_chat_request_failed")
                );
            }
        },

    fetchChannelReportData:
        (
            channelId: string
        ): AppThunk<
            Promise<{
                reportGeneratedAt: string | null;
                vizCharts: IVisualizationChart[];
                totalNumbersStats: ITotalNumbersStats | null;
            }>
        > =>
        async () => {
            const response = await ReportService.getChannelSummary(channelId);
            return {
                reportGeneratedAt: response.report_generated_at ?? null,
                vizCharts: response.visualization_charts ?? [],
                totalNumbersStats: response.total_numbers_stats ?? null,
            };
        },

    closeChannel:
        (channel: IChannel): AppThunk<Promise<void>> =>
        async (dispatch, getState) => {
            try {
                // Use consolidated PUT endpoint to close channel (includes report eligibility check)
                const closeResponse: UpdateChannelResponse = await putChannel(
                    channel,
                    {
                        closed: true,
                        active: false,
                    }
                );

                dispatch(
                    updateChannel({
                        ...channel,
                        closed: true,
                        active: false,
                    })
                );

                const reportFlow = closeResponse?.reportFlow;
                const accountType = getState().auth.user.account?.type;
                const canUseReportFlow = hasPremiumFeatures(accountType);

                if (canUseReportFlow && reportFlow?.eligible) {
                    dispatch(
                        dialogActions.openDialog({
                            id: DialogComponents.AiReportContextDialog,
                            props: {
                                channelId: channel._id,
                                maxWidth: "sm",
                            },
                        })
                    );
                    return;
                }

                if (
                    canUseReportFlow &&
                    reportFlow?.eligible === false &&
                    reportFlow?.reason === "insufficient_data"
                ) {
                    dispatch(
                        dialogActions.openDialog({
                            id: DialogComponents.InsufficientReportDataDialog,
                            props: {
                                maxWidth: "sm",
                            },
                        })
                    );
                    return;
                }

                dispatch(dialogActions.hideDialog());
            } catch (error) {
                dispatch(dialogActions.hideDialog());
                dispatch(
                    NotificationActions.notification({
                        snackbarOpen: true,
                        snackbarType: "error",
                        snackbarMessage: getErrorMessage(
                            error,
                            i18n.t("dialogs.channel.close_event_error")
                        ),
                    })
                );
            }
        },

    generateChannelReport:
        (channelId: string, aiContext?: string): AppThunk<Promise<void>> =>
        async (dispatch) => {
            try {
                await ReportService.postChannelReport(channelId, aiContext);
            } catch (error) {
                dispatch(
                    NotificationActions.notification({
                        snackbarOpen: true,
                        snackbarType: "error",
                        snackbarMessage: getErrorMessage(
                            error,
                            i18n.t("dialogs.channel.generate_report_error")
                        ),
                    })
                );
            }
        },

    updatePollTextField:
        (
            pollTextFieldId: string,
            channelId: string,
            question: string
        ): AppThunk<Promise<void>> =>
        async (dispatch) => {
            try {
                await updatePollTextFieldApi(pollTextFieldId, { question });
                await dispatch(ChannelActions.fetchChannelPolls(channelId));
            } catch (error) {
                console.error(error);
            }
        },
    deletePollTextField:
        (
            pollTextFieldId: string,
            channelId?: string
        ): AppThunk<Promise<void>> =>
        async (dispatch) => {
            try {
                await deletePollTextFieldApi(pollTextFieldId);
                if (channelId) {
                    await dispatch(ChannelActions.fetchChannelPolls(channelId));
                }
            } catch (error) {
                console.error(error);
            }
        },

    createSharedLink:
        (payload: CreateSharedLinkPayload) => async (dispatch: Dispatch) => {
            try {
                dispatch(isChannelActionLoading());

                const requestPayload: CreateSharedLinkRequest = {};

                if (payload.channel_id) {
                    requestPayload.channel_id = payload.channel_id;
                }

                if (payload.poll_id) {
                    requestPayload.poll_id = payload.poll_id;
                }

                const {
                    data: { link_id },
                } = await postSharedLink(requestPayload);

                dispatch(
                    storeSharedLinkInChannel({
                        link_id,
                        channel_id: payload.channel_id || "",
                    })
                );
                const { protocol, host } = window.location;

                dispatch(
                    dialogActions.openDialog({
                        id: payload?.dialog_id as string,
                        props: {
                            link: `${protocol}//${host}/share?ch=${link_id}`,
                            openDialog: true,
                            ...payload,
                        },
                    })
                );
                dispatch(isChannelActionLoaded());
            } catch (error) {
                dispatch(isChannelActionLoaded());
                dispatch(
                    NotificationActions.notification({
                        snackbarOpen: true,
                        snackbarType: "error",
                        snackbarMessage: getErrorMessage(
                            error,
                            i18n.t("snackbar_fail")
                        ),
                    })
                );
                console.error(error);
            }
        },

    getLinkChannelData:
        (linkId: string) =>
        async (dispatch: Dispatch): Promise<IChannel | undefined> => {
            const redirectToHostRoot = () => {
                window.location.replace(`${window.location.origin}/`);
            };

            try {
                const { data } = await getLinkData(linkId);

                if (!data?.channel) {
                    dispatch(isChannelActionLoaded());
                    redirectToHostRoot();
                    return undefined;
                }

                const normalizeSubChannel = (subChannel: any) => {
                    if (!subChannel) {
                        return subChannel;
                    }

                    return {
                        ...subChannel,
                        link_id:
                            subChannel.link_id ??
                            subChannel.linkId ??
                            subChannel.share_id ??
                            subChannel._id,
                    };
                };

                const rawSubChannels =
                    data?.channel?.sub_channels ??
                    data?.sub_channels ??
                    data?.children ??
                    [];

                const normalizedSubChannels = Array.isArray(rawSubChannels)
                    ? filterActiveChannels(rawSubChannels).map(
                          normalizeSubChannel
                      )
                    : [];

                const normalizedChannel = {
                    ...data.channel,
                    sub_channels: normalizedSubChannels,
                    has_children:
                        data.channel?.has_children ??
                        normalizedSubChannels.length > 0,
                };

                const pollState = data.polls.map((poll: any) => ({
                    ...poll,
                    comments: {
                        //@todo refactor avoid repeation
                        count: poll.comments.length,
                        list: [],
                        isLoading: false,
                        isExpanded: false,
                        loadMore: false,
                    },
                }));

                const hydratedChannel = {
                    ...normalizedChannel,
                    polls: pollState,
                };

                dispatch(storeChannels([hydratedChannel]));
                dispatch(
                    storeInvitationChannel({
                        ...normalizedChannel,
                        linkId: linkId,
                    })
                );

                dispatch(isChannelActionLoaded());

                // Loading a link is an arrival at its event: the QR, the automatic sub-event
                // pick and the lobby all land here. Not awaited, so it never delays the voter.
                postLinkScan(linkId);

                return hydratedChannel;
            } catch (error) {
                const message =
                    // @ts-ignore
                    error?.response?.data?.message ||
                    // @ts-ignore
                    error?.message;

                const inactiveRedirectUrl: unknown =
                    // @ts-ignore
                    error?.response?.data?.completion_redirect_url;
                const inactiveRedirectTarget =
                    typeof inactiveRedirectUrl === "string"
                        ? resolveRedirectTarget(inactiveRedirectUrl)
                        : null;

                // Unpublished event with a completion redirect: send participants straight on, like a
                // closed event, instead of showing the error and bouncing them to the home page.
                // replace, so Back doesn't land on a link that redirects again.
                if (message === "channel_inactive" && inactiveRedirectTarget) {
                    window.location.replace(inactiveRedirectTarget);
                    return undefined;
                }

                if (message === "channel_inactive") {
                    // User sees this when opening a shared link to an unpublished/inactive channel.
                    dispatch(
                        NotificationActions.notification({
                            snackbarOpen: true,
                            snackbarType: "error",
                            snackbarMessage:
                                "This event is unpublished right now.",
                        })
                    );
                }

                redirectToHostRoot();
                dispatch(isChannelActionLoaded());
                return undefined;
            }
        },
    resetJoinChannel: () => async (dispatch: Dispatch) => {
        try {
            dispatch(removeInvitationChannel());
        } catch (error) {
            console.error(error);
        }
    },
};

export default ChannelActions;
